// ✅ src/pages/VehicleV2/VehicleDailyProgressEntrySLT.jsx  (UPDATED FULL FILE)
//
// Backend expected:
//   app.use("/api/dailyProgressEntrySLT", dailyProgressEntrySLT);
//
// APIs used:
//   GET  http://nissi-india.com/api/dailyProgressEntrySLT/vehicle-header?branchId=&vehicleId=&progressId=
//   GET  http://nissi-india.com/api/dailyProgressEntrySLT/init?loginUserId=&branchId=&vehicleId=&progressId=
//   POST http://nissi-india.com/api/dailyProgressEntrySLT/save
//   GET  http://nissi-india.com/api/dailyProgressEntrySLT/search?loginUserId=&branchId=&vehicleId=&fromDate=&toDate=&month=&year=&locationId=
//   POST http://nissi-india.com/api/dailyProgressEntrySLT/delete
//
// ✅ Updated pattern:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - loading + pageLoading + saving + searching + deleting states
// - vehicleId from query/state/params
// - safe mapping for header/init/search response
// - same UI structure style
// - submit sends clean payload
// - reset/search/delete improved

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaSearch, FaTrash, FaUndo } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/dailyProgressEntrySLT";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toText = (v, d = "") => {
  if (v === null || v === undefined) return d;
  return String(v);
};

const clean = (v) => String(v ?? "").trim();

const toStr = (v, d = "") =>
  v === null || v === undefined ? d : String(v).trim();

const pad2 = (n) => String(n).padStart(2, "0");

const todayDDMMYYYY = () => {
  const dt = new Date();
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const parseDDMMYYYY = (s) => {
  const parts = String(s || "").split("/");
  if (parts.length !== 3) return null;

  const [dd, mm, yyyy] = parts;
  const d = Number(dd);
  const m = Number(mm);
  const y = Number(yyyy);

  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
    return null;
  }

  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }

  return dt;
};

const isValidDDMMYYYY = (s) => !!parseDDMMYYYY(s);

const parseAnyDateToDDMMYYYY = (raw) => {
  const val = clean(raw);
  if (!val) return "";

  if (isValidDDMMYYYY(val)) return val;

  const dt = new Date(val);
  if (Number.isNaN(dt.getTime())) return "";

  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const VehicleDailyProgressEntrySLT = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const routerLocation = useLocation();
  const params = useParams();

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const vehicleId = useMemo(() => {
    const sp = new URLSearchParams(routerLocation?.search || "");

    return (
      toInt(routerLocation?.state?.vehicleId, 0) ||
      toInt(routerLocation?.state?.id, 0) ||
      toInt(params?.vehicleId, 0) ||
      toInt(params?.id, 0) ||
      toInt(sp.get("vehicleId"), 0) ||
      toInt(sp.get("id"), 0) ||
      0
    );
  }, [routerLocation, params]);

  const progressIdFromSource = useMemo(() => {
    const sp = new URLSearchParams(routerLocation?.search || "");

    return (
      toInt(routerLocation?.state?.progressId, 0) ||
      toInt(params?.progressId, 0) ||
      toInt(sp.get("progressId"), 0) ||
      0
    );
  }, [routerLocation, params]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [deletingId, setDeletingId] = useState(0);

  const [vehicleInfo, setVehicleInfo] = useState({
    vehicleId: vehicleId || 0,
    vehicleCode: "",
    vehicleRegNo: "",
    branchName: "",
    vehicleType: "",
    vehicleSubtype: "",
    vehicleBasis: "",
  });

  const [locations, setLocations] = useState([]);

  const [months, setMonths] = useState([
    { id: "01", name: "January" },
    { id: "02", name: "February" },
    { id: "03", name: "March" },
    { id: "04", name: "April" },
    { id: "05", name: "May" },
    { id: "06", name: "June" },
    { id: "07", name: "July" },
    { id: "08", name: "August" },
    { id: "09", name: "September" },
    { id: "10", name: "October" },
    { id: "11", name: "November" },
    { id: "12", name: "December" },
  ]);

  const [years, setYears] = useState(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 8 }).map((_, i) => ({
      id: String(y - 4 + i),
      name: String(y - 4 + i),
    }));
  });

  const [form, setForm] = useState({
    date: todayDDMMYYYY(),
    locationId: "",
    reasonForIdle: "",
    remarks: "",
    startingKm: "",
    startingHours: "",
    usageManPowerCost: "",
    driverName: "",
    fuelTankCapacity: "",
    fuelFilled: "",
    fuelConsumption: "",
    status: "Running",
    natureOfWork: "",
    noOfTonnesM3: "",
    closingKm: "",
    closingHours: "",
    productionQty: "",
    noOfTrips: "",
    noOfLocations: "",
    openingFuel: "",
    totalFuelInTank: "",
    closingFuelInTank: "",
    progressId: progressIdFromSource || 0,
  });

  const [filter, setFilter] = useState({
    fromDate: "",
    toDate: "",
    month: "",
    year: "",
    locationId: "",
  });

  const [list, setList] = useState([]);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const cardClass = `p-6 mt-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-white ${currentSkin.glass} text-black`
  }`;

  const sectionTitleClass = `px-3 py-2 text-sm font-semibold border-b mb-4 ${
    isDark ? "border-white/10" : "border-gray-200"
  }`;

  const setField = useCallback((key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
  }, []);

  const setFilterField = useCallback((key, val) => {
    setFilter((p) => ({ ...p, [key]: val }));
  }, []);

  const mapVehicleHeader = useCallback(
    (raw) => {
      const r = raw || {};
      return {
        vehicleId: toInt(
          r?.vehicleId ?? r?.fld_vehicleid ?? vehicleId ?? 0,
          vehicleId || 0
        ),
        vehicleCode: toText(r?.vehicleCode ?? r?.fld_vehicle_code),
        vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
        branchName: toText(r?.branchName ?? r?.fld_branchname),
        vehicleType: toText(r?.vehicleType ?? r?.fld_vehicletypename),
        vehicleSubtype: toText(
          r?.vehicleSubtype ?? r?.fld_vehicle_subtypename
        ),
        vehicleBasis: toText(r?.vehicleBasis ?? r?.fld_vehicle_basistype),
      };
    },
    [vehicleId]
  );

  const mapLocation = useCallback((l) => {
    const raw = l || {};
    return {
      id: toText(
        raw?.id ?? raw?.locationId ?? raw?.fld_Worklocationid ?? raw?.value
      ),
      name: toText(
        raw?.name ??
          raw?.locationName ??
          raw?.fld_Worklocationname ??
          raw?.text
      ),
      _raw: raw,
    };
  }, []);

  const mapEntry = useCallback((e) => {
    const raw = e || {};
    return {
      date: parseAnyDateToDDMMYYYY(raw?.date),
      locationId: toText(raw?.locationId ?? raw?.fld_Worklocationid),
      status: toText(raw?.status, "Running"),
      reasonForIdle: toText(raw?.reasonForIdle),
      natureOfWork: toText(raw?.natureOfWork),
      remarks: toText(raw?.remarks),
      startingKm: toText(raw?.startingKm),
      closingKm: toText(raw?.closingKm),
      startingHours: toText(raw?.startingHours),
      closingHours: toText(raw?.closingHours),
      usageManPowerCost: toText(raw?.usageManPowerCost),
      productionQty: toText(raw?.productionQty),
      noOfTrips: toText(raw?.noOfTrips),
      noOfLocations: toText(raw?.noOfLocations),
      noOfTonnesM3: toText(raw?.noOfTonnesM3),
      driverName: toText(raw?.driverName),
      fuelTankCapacity: toText(raw?.fuelTankCapacity),
      fuelFilled: toText(raw?.fuelFilled),
      fuelConsumption: toText(raw?.fuelConsumption),
      openingFuel: toText(raw?.openingFuel),
      totalFuelInTank: toText(raw?.totalFuelInTank),
      closingFuelInTank: toText(raw?.closingFuelInTank),
      progressId: toInt(raw?.progressId, 0),
    };
  }, []);

  const mapSearchRow = useCallback((r) => {
    const raw = r || {};
    return {
      progressId: toInt(raw?.progressId ?? raw?.id ?? raw?.fld_progressid, 0),
      date: toText(raw?.date ?? raw?.fld_date),
      locationName: toText(
        raw?.locationName ?? raw?.location ?? raw?.fld_Worklocationname
      ),
      status: toText(raw?.status),
      startingKm: toText(raw?.startingKm),
      closingKm: toText(raw?.closingKm),
      startingHours: toText(raw?.startingHours),
      closingHours: toText(raw?.closingHours),
      noOfTonnesM3: toText(raw?.noOfTonnesM3),
      productionQty: toText(raw?.productionQty),
      noOfTrips: toText(raw?.noOfTrips),
      fuelFilled: toText(raw?.fuelFilled),
      _raw: raw,
    };
  }, []);

  /* ---------------- fetch header ---------------- */
  const fetchVehicleHeader = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(`${API_BASE}/vehicle-header`, {
        params: {
          branchId: toInt(branchId, 0),
          vehicleId: toInt(vehicleId, 0),
          progressId: toInt(progressIdFromSource, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Vehicle header load failed");
      }

      const header = mapVehicleHeader(data?.vehicleInfo || data?.header || data);
      setVehicleInfo(header);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Vehicle header load failed"
      );
    } finally {
      setLoading(false);
    }
  }, [branchId, vehicleId, progressIdFromSource, commonHeaders, mapVehicleHeader]);

  /* ---------------- init ---------------- */
  const fetchInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          vehicleId: toInt(vehicleId, 0),
          progressId: toInt(progressIdFromSource, 0),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Init failed");
      }

      const safeLocations = Array.isArray(data?.locations)
        ? data.locations.map(mapLocation)
        : [];
      setLocations(safeLocations);

      if (Array.isArray(data?.months) && data.months.length) {
        setMonths(
          data.months.map((m) => ({
            id: toText(m?.id ?? m?.value ?? m?.monthId),
            name: toText(m?.name ?? m?.text ?? m?.monthName),
          }))
        );
      }

      if (Array.isArray(data?.years) && data.years.length) {
        setYears(
          data.years.map((y) => ({
            id: toText(y?.id ?? y?.value ?? y?.yearId ?? y?.name),
            name: toText(y?.name ?? y?.text ?? y?.yearName ?? y?.id),
          }))
        );
      }

      if (data?.entry) {
        const entry = mapEntry(data.entry);
        setForm((p) => ({
          ...p,
          ...entry,
          progressId:
            toInt(entry.progressId, 0) || toInt(p.progressId, 0) || 0,
        }));
      } else if (data?.progressId) {
        setForm((p) => ({
          ...p,
          progressId: toInt(data.progressId, p.progressId),
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    vehicleId,
    progressIdFromSource,
    commonHeaders,
    mapLocation,
    mapEntry,
  ]);

  /* ---------------- validate ---------------- */
  const validate = useCallback(() => {
    if (!clean(form.date)) return "Date is required";
    if (!isValidDDMMYYYY(form.date)) return "Date must be in dd/MM/yyyy format";
    if (!clean(form.locationId)) return "Location is required";
    if (!clean(form.status)) return "Status is required";

    if (!clean(form.startingKm)) return "Starting KM is required";
    if (!clean(form.closingKm)) return "Closing KM is required";
    if (!clean(form.startingHours)) return "Starting Hours is required";
    if (!clean(form.closingHours)) return "Closing Hours is required";

    if (!clean(form.usageManPowerCost))
      return "Usage/Man Power Cost is required";
    if (!clean(form.noOfTonnesM3)) return "No. of Tonnes/M3 is required";
    if (!clean(form.productionQty)) return "Production Qty is required";
    if (!clean(form.noOfLocations)) return "No of Locations is required";

    if (!clean(form.fuelFilled)) return "Fuel Filled is required";
    if (!clean(form.openingFuel)) return "Opening Fuel is required";
    if (!clean(form.totalFuelInTank)) return "Total Fuel in Tank is required";
    if (!clean(form.closingFuelInTank))
      return "Closing Fuel in Tank is required";

    return "";
  }, [form]);

  /* ---------------- submit ---------------- */
  const submitEntry = useCallback(async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        vehicleId: toInt(vehicleInfo.vehicleId || vehicleId, 0),
        progressId: toInt(form.progressId, 0),
        date: clean(form.date),
        locationId: clean(form.locationId),
        status: clean(form.status),
        reasonForIdle: clean(form.reasonForIdle),
        natureOfWork: clean(form.natureOfWork),
        remarks: clean(form.remarks),
        startingKm: clean(form.startingKm),
        closingKm: clean(form.closingKm),
        startingHours: clean(form.startingHours),
        closingHours: clean(form.closingHours),
        usageManPowerCost: clean(form.usageManPowerCost),
        driverName: clean(form.driverName),
        noOfTonnesM3: clean(form.noOfTonnesM3),
        productionQty: clean(form.productionQty),
        noOfTrips: clean(form.noOfTrips),
        noOfLocations: clean(form.noOfLocations),
        fuelTankCapacity: clean(form.fuelTankCapacity),
        fuelFilled: clean(form.fuelFilled),
        fuelConsumption: clean(form.fuelConsumption),
        openingFuel: clean(form.openingFuel),
        totalFuelInTank: clean(form.totalFuelInTank),
        closingFuelInTank: clean(form.closingFuelInTank),
      };

      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      const newId = toInt(data?.progressId, 0);
      if (newId) {
        setForm((p) => ({ ...p, progressId: newId }));
      }

      toast.success(data?.message || "Saved successfully");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [
    validate,
    loginUserId,
    branchId,
    vehicleInfo.vehicleId,
    vehicleId,
    form,
    commonHeaders,
  ]);

  /* ---------------- reset ---------------- */
  const resetForm = useCallback(() => {
    setForm((p) => ({
      ...p,
      date: todayDDMMYYYY(),
      locationId: "",
      reasonForIdle: "",
      remarks: "",
      startingKm: "",
      startingHours: "",
      usageManPowerCost: "",
      driverName: "",
      fuelTankCapacity: "",
      fuelFilled: "",
      fuelConsumption: "",
      status: "Running",
      natureOfWork: "",
      noOfTonnesM3: "",
      closingKm: "",
      closingHours: "",
      productionQty: "",
      noOfTrips: "",
      noOfLocations: "",
      openingFuel: "",
      totalFuelInTank: "",
      closingFuelInTank: "",
    }));
  }, []);

  /* ---------------- search ---------------- */
  const searchEntries = useCallback(async () => {
    try {
      setSearching(true);

      const { data } = await axios.get(`${API_BASE}/search`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          vehicleId: toInt(vehicleInfo.vehicleId || vehicleId, 0),
          fromDate: clean(filter.fromDate),
          toDate: clean(filter.toDate),
          month: clean(filter.month),
          year: clean(filter.year),
          locationId: clean(filter.locationId),
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Search failed");
      }

      const safeList = Array.isArray(data?.list)
        ? data.list.map(mapSearchRow)
        : [];
      setList(safeList);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Search failed"
      );
      setList([]);
    } finally {
      setSearching(false);
    }
  }, [
    loginUserId,
    branchId,
    vehicleInfo.vehicleId,
    vehicleId,
    filter,
    commonHeaders,
    mapSearchRow,
  ]);

  /* ---------------- delete ---------------- */
  const deleteEntry = useCallback(
    async (delProgressId) => {
      const pid = toInt(delProgressId, 0);
      if (!pid) {
        toast.error("progressId missing");
        return;
      }

      try {
        setDeletingId(pid);

        const { data } = await axios.post(
          `${API_BASE}/delete`,
          {
            progressId: pid,
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
          },
          {
            headers: commonHeaders,
          }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Delete failed");
        }

        toast.success(data?.message || "Deleted successfully");
        await searchEntries();
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "Delete failed"
        );
      } finally {
        setDeletingId(0);
      }
    },
    [loginUserId, branchId, commonHeaders, searchEntries]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    if (!vehicleId) {
      toast.error("Vehicle ID missing. Open from listing again.");
      return;
    }

    if (!toInt(branchId, 0) || !toInt(loginUserId, 0)) {
      toast.error("Session missing (branchId/loginUserId). Please login again.");
      return;
    }

    fetchVehicleHeader();
    fetchInit();
  }, [
    branchId,
    loginUserId,
    vehicleId,
    progressIdFromSource,
    fetchVehicleHeader,
    fetchInit,
  ]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE DAILY PROGRESS ENTRY
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        {/* MAIN CARD */}
        <div className={cardClass}>
          {/* Top row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Daily Progress Entry
              </h1>
            </div>

            {(loading || pageLoading || saving) && (
              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {saving ? "Saving..." : "Loading..."}
              </div>
            )}
          </div>

          {/* HEADER INFO */}
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex gap-2">
                <b>Vehicle Code</b>
                <span>:</span>
                <span>{vehicleInfo.vehicleCode || "-"}</span>
              </div>
              <div className="flex gap-2">
                <b>Vehicle RegNo</b>
                <span>:</span>
                <span>{vehicleInfo.vehicleRegNo || "-"}</span>
              </div>
              <div className="flex gap-2">
                <b>Branch Name</b>
                <span>:</span>
                <span>{vehicleInfo.branchName || "-"}</span>
              </div>
              <div className="flex gap-2">
                <b>Vehicle Type</b>
                <span>:</span>
                <span>{vehicleInfo.vehicleType || "-"}</span>
              </div>
              <div className="flex gap-2">
                <b>Vehicle Subtype</b>
                <span>:</span>
                <span>{vehicleInfo.vehicleSubtype || "-"}</span>
              </div>
              <div className="flex gap-2">
                <b>Vehicle Basis</b>
                <span>:</span>
                <span>{vehicleInfo.vehicleBasis || "-"}</span>
              </div>
            </div>
          </div>

          <div className={sectionTitleClass}>VEHICLE POSITION ENTRY</div>

          {/* FORM */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-4">
            {/* LEFT */}
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Date *</label>
                <input
                  className={inputClass}
                  value={form.date}
                  onChange={(e) => setField("date", e.target.value)}
                  placeholder="dd/MM/yyyy"
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>Location *</label>
                <select
                  className={inputClass}
                  value={form.locationId}
                  onChange={(e) => setField("locationId", e.target.value)}
                  disabled={saving || loading || pageLoading}
                >
                  <option value="">Select</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Reason For Idle</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.reasonForIdle}
                  onChange={(e) => setField("reasonForIdle", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>Remarks</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setField("remarks", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>StartingKM *</label>
                <input
                  className={inputClass}
                  value={form.startingKm}
                  onChange={(e) => setField("startingKm", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>StartingHours *</label>
                <input
                  className={inputClass}
                  value={form.startingHours}
                  onChange={(e) => setField("startingHours", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>Usage/Man Power/Cost *</label>
                <input
                  className={inputClass}
                  value={form.usageManPowerCost}
                  onChange={(e) =>
                    setField("usageManPowerCost", e.target.value)
                  }
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>DriverName</label>
                <input
                  className={inputClass}
                  value={form.driverName}
                  onChange={(e) => setField("driverName", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Fuel Tank Capacity</label>
                  <input
                    className={inputClass}
                    value={form.fuelTankCapacity}
                    onChange={(e) =>
                      setField("fuelTankCapacity", e.target.value)
                    }
                    disabled={saving || loading || pageLoading}
                  />
                </div>
                <div>
                  <label className={labelClass}>Fuel Filled *</label>
                  <input
                    className={inputClass}
                    value={form.fuelFilled}
                    onChange={(e) => setField("fuelFilled", e.target.value)}
                    disabled={saving || loading || pageLoading}
                  />
                </div>
                <div>
                  <label className={labelClass}>Fuel Consumption</label>
                  <input
                    className={inputClass}
                    value={form.fuelConsumption}
                    onChange={(e) =>
                      setField("fuelConsumption", e.target.value)
                    }
                    disabled={saving || loading || pageLoading}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Status *</label>
                <select
                  className={inputClass}
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                  disabled={saving || loading || pageLoading}
                >
                  <option value="Running">Running</option>
                  <option value="Idle">Idle</option>
                  <option value="Breakdown">Breakdown</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Nature Of Work</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.natureOfWork}
                  onChange={(e) => setField("natureOfWork", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>No.of Tonnes/M3 *</label>
                <input
                  className={inputClass}
                  value={form.noOfTonnesM3}
                  onChange={(e) => setField("noOfTonnesM3", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>ClosingKM *</label>
                <input
                  className={inputClass}
                  value={form.closingKm}
                  onChange={(e) => setField("closingKm", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>ClosingHours *</label>
                <input
                  className={inputClass}
                  value={form.closingHours}
                  onChange={(e) => setField("closingHours", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>Production Qty *</label>
                <input
                  className={inputClass}
                  value={form.productionQty}
                  onChange={(e) => setField("productionQty", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>No of Trips</label>
                <input
                  className={inputClass}
                  value={form.noOfTrips}
                  onChange={(e) => setField("noOfTrips", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div>
                <label className={labelClass}>No of Locations *</label>
                <input
                  className={inputClass}
                  value={form.noOfLocations}
                  onChange={(e) => setField("noOfLocations", e.target.value)}
                  disabled={saving || loading || pageLoading}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Opening Fuel *</label>
                  <input
                    className={inputClass}
                    value={form.openingFuel}
                    onChange={(e) => setField("openingFuel", e.target.value)}
                    disabled={saving || loading || pageLoading}
                  />
                </div>
                <div>
                  <label className={labelClass}>Total Fuel in Tank *</label>
                  <input
                    className={inputClass}
                    value={form.totalFuelInTank}
                    onChange={(e) =>
                      setField("totalFuelInTank", e.target.value)
                    }
                    disabled={saving || loading || pageLoading}
                  />
                </div>
                <div>
                  <label className={labelClass}>Closing Fuel in Tank *</label>
                  <input
                    className={inputClass}
                    value={form.closingFuelInTank}
                    onChange={(e) =>
                      setField("closingFuelInTank", e.target.value)
                    }
                    disabled={saving || loading || pageLoading}
                  />
                </div>
              </div>

              <div className="text-xs opacity-80 pt-1">
                <span className="mr-4">
                  <b>vehicleId</b>: {vehicleInfo.vehicleId || vehicleId || 0}
                </span>
                <span>
                  <b>progressId</b>: {toInt(form.progressId, 0)}
                </span>
              </div>
            </div>
          </div>

          {/* BUTTONS */}
          <div className="flex justify-center mt-8 gap-4">
            <button
              onClick={submitEntry}
              disabled={saving || loading || pageLoading}
              className={`flex items-center gap-2 px-8 py-2 rounded-lg text-white shadow ${buttonClass} ${hoverClass} ${
                saving || loading || pageLoading
                  ? "opacity-70 cursor-not-allowed"
                  : ""
              }`}
            >
              <FaSave />
              {saving ? "Saving..." : "Submit"}
            </button>

            <button
              onClick={resetForm}
              disabled={saving || loading || pageLoading}
              className={`flex items-center gap-2 px-8 py-2 rounded-lg border ${
                isDark ? "border-white/20" : "border-gray-400"
              } ${
                saving || loading || pageLoading
                  ? "opacity-70 cursor-not-allowed"
                  : ""
              }`}
            >
              <FaUndo />
              Reset
            </button>
          </div>
        </div>

        {/* FILTER AREA */}
        <div className={cardClass}>
          <div className={sectionTitleClass}>FILTRATION AREA</div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-4">
            <div>
              <label className={labelClass}>FromDate</label>
              <input
                className={inputClass}
                value={filter.fromDate}
                onChange={(e) => setFilterField("fromDate", e.target.value)}
                placeholder="dd/MM/yyyy"
                disabled={searching}
              />
            </div>

            <div>
              <label className={labelClass}>ToDate</label>
              <input
                className={inputClass}
                value={filter.toDate}
                onChange={(e) => setFilterField("toDate", e.target.value)}
                placeholder="dd/MM/yyyy"
                disabled={searching}
              />
            </div>

            <div>
              <label className={labelClass}>Month</label>
              <select
                className={inputClass}
                value={filter.month}
                onChange={(e) => setFilterField("month", e.target.value)}
                disabled={searching}
              >
                <option value="">Select</option>
                {months.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Year</label>
              <select
                className={inputClass}
                value={filter.year}
                onChange={(e) => setFilterField("year", e.target.value)}
                disabled={searching}
              >
                <option value="">Select</option>
                {years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Location</label>
              <select
                className={inputClass}
                value={filter.locationId}
                onChange={(e) => setFilterField("locationId", e.target.value)}
                disabled={searching}
              >
                <option value="">Select</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={searchEntries}
                disabled={searching}
                className={`flex items-center gap-2 px-10 py-2 rounded-lg text-white shadow ${buttonClass} ${hoverClass} disabled:opacity-60`}
              >
                <FaSearch />
                {searching ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* RESULTS TABLE */}
          <div className="mt-6 border rounded-lg overflow-hidden">
            <div
              className={`px-3 py-2 text-sm font-semibold ${
                isDark ? "bg-white/10" : "bg-gray-100"
              }`}
            >
              Results
            </div>

            {searching ? (
              <div className="p-4 text-sm opacity-80">Searching...</div>
            ) : list.length === 0 ? (
              <div className="p-4 text-sm opacity-80">No Records Found</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-[1200px] w-full text-sm">
                  <thead className={isDark ? "bg-white/10" : "bg-gray-50"}>
                    <tr>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Location</th>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Start KM</th>
                      <th className="p-2 text-left">Close KM</th>
                      <th className="p-2 text-left">Start Hrs</th>
                      <th className="p-2 text-left">Close Hrs</th>
                      <th className="p-2 text-left">Tonnes/M3</th>
                      <th className="p-2 text-left">Prod Qty</th>
                      <th className="p-2 text-left">Trips</th>
                      <th className="p-2 text-left">Fuel Filled</th>
                      <th className="p-2 text-left">ProgressId</th>
                      <th className="p-2 text-left">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {list.map((r, idx) => (
                      <tr
                        key={r.progressId || idx}
                        className={isDark ? "border-t border-white/10" : "border-t"}
                      >
                        <td className="p-2">{r.date}</td>
                        <td className="p-2">{r.locationName}</td>
                        <td className="p-2">{r.status}</td>
                        <td className="p-2">{r.startingKm}</td>
                        <td className="p-2">{r.closingKm}</td>
                        <td className="p-2">{r.startingHours}</td>
                        <td className="p-2">{r.closingHours}</td>
                        <td className="p-2">{r.noOfTonnesM3}</td>
                        <td className="p-2">{r.productionQty}</td>
                        <td className="p-2">{r.noOfTrips}</td>
                        <td className="p-2">{r.fuelFilled}</td>
                        <td className="p-2">{r.progressId}</td>
                        <td className="p-2">
                          <button
                            onClick={() => deleteEntry(r.progressId)}
                            disabled={deletingId === r.progressId}
                            className={`flex items-center gap-2 px-3 py-1 rounded text-white ${buttonClass} ${hoverClass} ${
                              deletingId === r.progressId
                                ? "opacity-60 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <FaTrash />
                            {deletingId === r.progressId ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDailyProgressEntrySLT;